import {
    Button,
    Input,
    InputNumber,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table
} from "antd";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import axios from "utils/axios";
import configFile from "config.json";
import { NotificationManager } from "react-notifications";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";

const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

const formatUser = (value) => {
    if (!value) return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
        return (
            value.fullName ||
            value.name ||
            value.username ||
            value.email ||
            value.id ||
            "-"
        );
    }
    return "-";
};

const ItemCustomizationTab = ({
    menu,
    modifiers,
    definitions,
    onDefinitionChange,
    onDeleteDefinition,
    onAddDefinition,
}) => {
    const rows = definitions || [];
    const sizeOptions = useMemo(
        () =>
            (modifiers.sizes || []).map((size) => ({
                label: size.name,
                value: size.id,
            })),
        [modifiers.sizes]
    );
    const baseOptions = useMemo(
        () =>
            (modifiers.bases || []).map((base) => ({
                label: base.name,
                value: base.id,
            })),
        [modifiers.bases]
    );
    const handleSaveItemCustomization = useCallback(async (record) => {
        try {
            const isDraft = !record?.id || String(record.id).startsWith("draft_");
            const payload = {
                id: isDraft ? undefined : record.id,
                type: isDraft ? menu?.type : record.type,
                price: record.price ?? 0,
                pickupPrice: record.pickupPrice ?? 0,
                majorGroupId: record.majorGroupId,
                masterName: record.masterName,
                miMasterObjNum: record.miMasterObjNum,
                sizeId: record.sizeId || undefined,
                baseId: record.baseId || undefined,
                menuId: record.menuId,
                ableMenu: record.ableMenu,
                ableCombo: record.ableCombo,
            };
            if (!isDraft) {
                await axios.post(`${API_BASE}/manageMenu/updateMenuDefinition`, payload);
            } else {
                const response = await axios.post(
                    `${API_BASE}/manageMenu/createMenuDefinition`,
                    payload
                );
                const createdDefinition = response?.data?.data;
                if (createdDefinition?.id) {
                    // Replace draft row ID with persisted ID to avoid duplicate creates on later edits.
                    onDefinitionChange(record.id, createdDefinition);
                } else {
                    console.warn('Created definition missing ID in response:', response?.data);
                }
            }
            NotificationManager.success("Амжилттай");
        } catch (err) {
            console.log(err);
            NotificationManager.error(err?.message || "Амжилтгүй");
        }
    }, [menu?.type, onDefinitionChange]);

    const handleDeleteItemCustomization = useCallback(async (record) => {
        const definitionId = record?.id;
        if (!definitionId) {
            return;
        }
        try {
            const isDraft = String(definitionId).startsWith("draft_");
            if (!isDraft) {
                await axios.post(`${API_BASE}/manageMenu/deleteMenuDefinition`, {
                    definitionId,
                });
            }
            onDeleteDefinition?.(definitionId);
            NotificationManager.success("Амжилттай устгалаа");
        } catch (err) {
            console.log(err);
            NotificationManager.error(err?.message || "Устгаж чадсангүй");
        }
    }, [onDeleteDefinition]);

    const columns = useMemo(
        () => [
            {
                title: "Master Name",
                dataIndex: "masterName",
                render: (value, record) => (
                    <Input
                        value={value}
                        placeholder="Master name"
                        onChange={(event) =>
                            onDefinitionChange(record.id, {
                                masterName: event.target.value,
                            })
                        }
                    />
                ),
                fixed: "left",
                width: 180,
            },
            {
                title: "Size",
                dataIndex: "sizeId",
                render: (value, record) => (
                    <Select
                        value={value ?? null}
                        placeholder="Select size"
                        allowClear
                        options={sizeOptions}
                        onChange={(nextValue) =>
                            onDefinitionChange(record.id, {
                                sizeId: nextValue ?? null,
                            })
                        }
                    />
                ),
                width: 140,
            },
            {
                title: "Base",
                dataIndex: "baseId",
                render: (value, record) => (
                    <Select
                        value={value ?? null}
                        placeholder="Select base"
                        allowClear
                        options={baseOptions}
                        onChange={(nextValue) =>
                            onDefinitionChange(record.id, {
                                baseId: nextValue ?? null,
                            })
                        }
                    />
                ),
                width: 130,
            },
            {
                title: "Очиж авах үнэ",
                dataIndex: "pickupPrice",
                render: (value, record) => (
                    <InputNumber
                        min={0}
                        value={value ?? 0}
                        onChange={(nextValue) =>
                            onDefinitionChange(record.id, { pickupPrice: nextValue ?? 0 })
                        }
                        style={{ width: "100%" }}
                    />
                ),
                width: 110,
            },
            {
                // title: "Price",
                title: "Delivery Price",
                dataIndex: "price",
                render: (value, record) => (
                    <InputNumber
                        min={0}
                        value={value ?? 0}
                        onChange={(nextValue) =>
                            onDefinitionChange(record.id, { price: nextValue ?? 0 })
                        }
                        style={{ width: "100%" }}
                    />
                ),
                width: 110,
            },
            {
                title: "POS ID",
                dataIndex: "miMasterObjNum",
                render: (value, record) => (
                    <Input
                        value={value}
                        onChange={(event) =>
                            onDefinitionChange(record.id, {
                                miMasterObjNum: event.target.value,
                            })
                        }
                    />
                ),
                width: 160,
            },
            {
                title: "Able Menu",
                dataIndex: "ableMenu",
                render: (value, record) => (
                    <Switch
                        checked={Boolean(value)}
                        onChange={(checked) =>
                            onDefinitionChange(record.id, { ableMenu: checked })
                        }
                    />
                ),
                width: 110,
            },
            {
                title: "Able Combo",
                dataIndex: "ableCombo",
                render: (value, record) => (
                    <Switch
                        checked={Boolean(value)}
                        onChange={(checked) =>
                            onDefinitionChange(record.id, { ableCombo: checked })
                        }
                    />
                ),
                width: 120,
            },
            {
                title: "Created At",
                dataIndex: "createdAt",
                render: (value) => formatDateTime(value),
                width: 170,
            },
            {
                title: "Updated At",
                dataIndex: "updatedAt",
                render: (value) => formatDateTime(value),
                width: 170,
            },
            {
                title: "Updated By",
                dataIndex: "updatedBy",
                render: (value) => formatUser(value),
                width: 150,
            },
            {
                title: "",
                key: "action",
                render: (value, record) => (
                    <Space size={8}>
                        <Button type="primary" onClick={() => handleSaveItemCustomization(record)}>
                            <SaveOutlined />
                        </Button>
                        <Popconfirm
                            title="Delete item customization?"
                            okText="Delete"
                            cancelText="Cancel"
                            onConfirm={() => handleDeleteItemCustomization(record)}
                        >
                            <Button danger>
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    </Space>
                ),
                width: 130,
                fixed: "right",
            },
        ],
        [
            baseOptions,
            handleDeleteItemCustomization,
            handleSaveItemCustomization,
            onDefinitionChange,
            sizeOptions,
        ]
    );

    return (
        <div>
            <HeaderRow style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="dashed" onClick={onAddDefinition}>
                    +
                </Button>
            </HeaderRow>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1700 }}
            />
        </div>
    );
};

export default ItemCustomizationTab;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;
